import React, { FC, useCallback, useRef, useState } from "react";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import classNames from "classnames/dedupe";
import {
  DropdownAlignment,
  DropdownToggleStrategy,
  NavItem,
  NavList,
  Typography,
} from "../../components";
import { ShoppingBagIcon } from "../../icons";
import Dropdown from "../../components/Utility/Dropdown/Dropdown";
import DropdownList from "../../components/Utility/Dropdown/DropdownList";
import MyAccountDropdownList from "../../components/MyAccount/HoverEntryPoint/MyAccountDropdownList";
import { forceRerenderCssAnimation } from "../../helpers";

interface IActions {
  onClick?: () => void;
}

interface IMenuIcons {
  locationActions?: IActions;
  accountActions?: IActions;
  favoriteActions?: IActions;
  bagActions?: IActions;
}

export const MenuIcons: FC<IMenuIcons> = ({
  favoriteActions,
  locationActions,
  accountActions,
  bagActions,
}) => {
  const [cartItemsCount, setCartItemsCount] = useState(0);
  const [cartAnim, setCartAnim] = useState(false);

  const animRef = useRef();
  const handlePurchaseToggle = useCallback(() => {
    setCartAnim(false);
    if (animRef?.current) {
      forceRerenderCssAnimation(animRef.current);
    }
    setCartAnim(true);
    setCartItemsCount(cartItemsCount + 1);
    bagActions?.onClick();
  }, [cartItemsCount]);

  const handleAnimEnd = useCallback(() => {
    setCartAnim(false);
  }, []);

  const endListener = useCallback(
    (node: HTMLElement, done: () => void) =>
      node.addEventListener("transitionend", done, false),
    [],
  );

  return (
    <NavList ariaLabel="User menu">
      <NavItem
        iconData={{
          type: "svg-icon",
          data: {
            icon: "LocationPinIcon",
          },
        }}
        type="icon"
        classes={{
          root: "header-location icon-holder icon-holder--mobile-small me-0",
        }}
        {...locationActions}
      />

      <Dropdown
        dropdownButton={
          <NavItem
            iconData={{
              type: "svg-icon",
              data: {
                icon: "AccountIcon",
              },
            }}
            type="icon"
            classes={{
              root: "header-user icon-holder icon-holder--mobile-small",
            }}
          />
        }
        alignment={DropdownAlignment.END}
        toggleStrategy={DropdownToggleStrategy.HOVER}
        {...accountActions}
      >
        <DropdownList animation="uds-dropdown-toggle">
          {({ setOpened }) => (
            <MyAccountDropdownList
              classes={{ root: "mt-3" }}
              onItemClick={() => setOpened(false)}
            />
          )}
        </DropdownList>
      </Dropdown>
      <Dropdown
        dropdownButton={
          <NavItem
            iconData={{
              type: "svg-icon",
              data: {
                icon: "HeartIconEmpty",
              },
            }}
            type="icon"
            classes={{
              root: "header-wishlist icon-holder icon-holder--mobile-small",
            }}
          />
        }
        toggleStrategy={DropdownToggleStrategy.NONE}
        alignment={DropdownAlignment.END}
        {...favoriteActions}
      >
        <DropdownList animation="uds-dropdown-toggle">
          {({ setOpened }) => (
            <MyAccountDropdownList
              classes={{ root: "mt-3" }}
              onItemClick={() => setOpened(false)}
            />
          )}
        </DropdownList>
      </Dropdown>
      <NavItem
        type="icon"
        classes={{
          root: "header-bag icon-holder icon-holder--mobile-small",
          link: "position-relative",
        }}
        linkChildren={
          <>
            <div className="position-relative">
              <ShoppingBagIcon />
              <SwitchTransition>
                <CSSTransition
                  key={String(cartItemsCount)}
                  classNames="uds-appear-fade"
                  addEndListener={endListener as any}
                  mountOnEnter
                  unmountOnExit
                >
                  <div>
                    <div className="pin pin--top-right" />
                  </div>
                </CSSTransition>
              </SwitchTransition>
            </div>
            <Typography
              ref={animRef}
              classes={{
                root: classNames("badge badge--circle text-color-white", {
                  "uds-bounce-down": cartAnim,
                }),
              }}
              onAnimationEnd={handleAnimEnd}
            >
              22
            </Typography>
          </>
        }
        onClick={handlePurchaseToggle}
      />
      <NavItem
        iconData={{
          type: "svg-icon",
          data: {
            icon: "HamburgerSmallIcon",
          },
        }}
        type="icon"
        classes={{
          root: "header-burger icon-holder icon-holder--mobile-small me-n5",
        }}
      />
    </NavList>
  );
};
